/**
 * Preset Manager Initializer
 *
 * Must run BEFORE header-group to prevent flash of default header.
 * Persists header, announcement bar, footer, and custom font presets across navigation.
 *
 * Reads configuration from window.ezupPresetConfig (set by theme.liquid)
 */

(function() {
  'use strict';

  var config = window.ezupPresetConfig || {};
  var STORAGE_KEY = 'ezup_page_presets';
  var URL_PARAM_MAP = {
    header_id: 'ezup_header_id',
    annoucement_bar_id: 'ezup_announcement_id',
    footer_id: 'ezup_footer_id',
    sections_id: 'ezup_sections_id',
    orders_tag: 'ezup_orders_tag',
    custom_heading_font: 'ezup_heading_font',
    custom_body_font: 'ezup_body_font'
  };

  /**
   * Custom font definitions for presets
   * Maps font IDs to their CSS font-family values
   */
  var CUSTOM_FONTS = {
    industry: {
      family: '"industry", sans-serif',
      weight: '400',
      style: 'normal',
      bolderWeight: '700'
    }
  };

  /**
   * Check if current page is the homepage
   */
  function isHomepage() {
    var path = window.location.pathname;
    return path === '/' || /^\/[a-z]{2}(-[a-z]{2})?\/?$/i.test(path);
  }

  /**
   * Get stored presets from sessionStorage
   */
  function getStoredPresets() {
    try {
      var stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Save presets to sessionStorage
   */
  function savePresets(presets) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
    } catch (e) {
      // Silently fail if sessionStorage is not available
    }
  }

  /**
   * Read preset overrides from the current URL
   */
  function getUrlPresetOverrides() {
    var url = new URL(window.location.href);
    var overrides = {};

    Object.keys(URL_PARAM_MAP).forEach(function(key) {
      var value = url.searchParams.get(URL_PARAM_MAP[key]);

      if (value !== null && value !== '') {
        overrides[key] = value;
      }
    });

    return overrides;
  }

  /**
   * Merge preset sources, ignoring null/blank overrides
   */
  function mergePresets(basePresets, overridePresets) {
    var merged = Object.assign({}, basePresets || {});

    Object.keys(overridePresets || {}).forEach(function(key) {
      var value = overridePresets[key];

      if (value !== null && value !== '') {
        merged[key] = value;
      }
    });

    return merged;
  }

  /**
   * Clear stored presets
   */
  function clearPresets() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // Silently fail
    }
  }

  /**
   * Initialize preset manager and return active presets
   */
  function init() {
    var pagePresets = config.pagePresets;
    var themeSettings = config.themeSettings || {};
    var urlPresetOverrides = getUrlPresetOverrides();

    // On homepage, clear all stored presets and return null
    if (isHomepage()) {
      clearPresets();
      return null;
    }

    // Check if current page has its own presets
    var hasPagePresets = pagePresets && (
      pagePresets.header_id ||
      pagePresets.annoucement_bar_id ||
      pagePresets.footer_id ||
      pagePresets.sections_id ||
      pagePresets.custom_heading_font ||
      pagePresets.custom_body_font ||
      pagePresets.orders_tag
    );

    if (hasPagePresets) {
      // Build preset object including font settings from theme settings
      var presets = {
        header_id: pagePresets.header_id || null,
        annoucement_bar_id: pagePresets.annoucement_bar_id || null,
        footer_id: pagePresets.footer_id || null,
        sections_id: pagePresets.sections_id || null,
        orders_tag: pagePresets.orders_tag || null
      };

      // Add font presets from theme settings if not using shopify default
      if (themeSettings.prograde_custom_heading_font &&
          themeSettings.prograde_custom_heading_font !== 'shopify_default') {
        presets.custom_heading_font = themeSettings.prograde_custom_heading_font;
      }
      if (themeSettings.prograde_custom_body_font &&
          themeSettings.prograde_custom_body_font !== 'shopify_default') {
        presets.custom_body_font = themeSettings.prograde_custom_body_font;
      }

      savePresets(presets);
      return presets;
    }

    // No page presets, use URL overrides first, then stored presets
    var storedPresets = getStoredPresets();

    if (Object.keys(urlPresetOverrides).length > 0) {
      var mergedPresets = mergePresets(storedPresets, urlPresetOverrides);
      savePresets(mergedPresets);
      return mergedPresets;
    }

    return storedPresets;
  }

  /**
   * Serialize active presets into URL params for internal links
   */
  function getPresetLinkParams(activePresets) {
    var params = {};

    if (!activePresets) {
      return params;
    }

    Object.keys(URL_PARAM_MAP).forEach(function(key) {
      var value = activePresets[key];

      if (value !== null && value !== undefined && value !== '') {
        params[URL_PARAM_MAP[key]] = value;
      }
    });

    return params;
  }

  function shouldDecorateInternalUrl(url) {
    var path = url.pathname || '';

    if (url.origin !== window.location.origin) {
      return false;
    }

    if (path === '/' || /^\/[a-z]{2}(-[a-z]{2})?\/?$/i.test(path)) {
      return false;
    }

    if (/^\/(?:cart|checkout)(?:\/|$)/i.test(path)) {
      return false;
    }

    return true;
  }

  function getDecoratedInternalHref(href, params) {
    if (!href || !params || Object.keys(params).length === 0) {
      return null;
    }

    if (/^(#|mailto:|tel:|javascript:)/i.test(href)) {
      return null;
    }

    var url;

    try {
      url = new URL(href, window.location.origin);
    } catch (e) {
      return null;
    }

    if (!shouldDecorateInternalUrl(url)) {
      return null;
    }

    Object.keys(params).forEach(function(paramKey) {
      if (!url.searchParams.has(paramKey)) {
        url.searchParams.set(paramKey, params[paramKey]);
      }
    });

    return url.toString();
  }

  function decorateInternalLinks(activePresets) {
    var params = getPresetLinkParams(activePresets);

    if (Object.keys(params).length === 0) {
      return;
    }

    document.querySelectorAll('a[href]').forEach(function(link) {
      var decoratedHref = getDecoratedInternalHref(link.getAttribute('href'), params);

      if (decoratedHref) {
        link.setAttribute('href', decoratedHref);
      }
    });
  }

  function attachInternalLinkPropagation(activePresets) {
    var params = getPresetLinkParams(activePresets);

    if (Object.keys(params).length === 0) {
      return;
    }

    document.addEventListener('click', function(event) {
      var link = event.target.closest('a[href]');

      if (!link) {
        return;
      }

      var decoratedHref = getDecoratedInternalHref(link.getAttribute('href'), params);

      if (decoratedHref) {
        link.setAttribute('href', decoratedHref);
      }
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        decorateInternalLinks(activePresets);
      });
    } else {
      decorateInternalLinks(activePresets);
    }
  }

  /**
   * Inject CSS to prevent flash of default headers/announcements/footers
   */
  function injectFlashPreventionCSS(activePresets, pagePresets) {
    // Only inject if we have stored presets but page doesn't have its own
    if (!activePresets || pagePresets) {
      return;
    }

    var css = '';
    var headerId = activePresets.header_id;
    var announcementId = activePresets.annoucement_bar_id;
    var footerId = activePresets.footer_id;
    var sectionsId = activePresets.sections_id;

    // Hide default sections and show preset sections
    if (headerId) {
      css += '[data-section-type="header"][data-preset-id="0"], [data-section-type="header"][data-preset-id=""] { display: none !important; }';
      css += '[data-section-type="header"][data-preset-id="' + headerId + '"][hidden] { display: block !important; }';
      css += '[data-section-type="header"][data-preset-id="' + headerId + '"] { display: block !important; }';
    }
    if (announcementId) {
      css += '[data-section-type="announcement-bar"][data-preset-id="0"], [data-section-type="announcement-bar"][data-preset-id=""] { display: none !important; }';
      css += '[data-section-type="announcement-bar"][data-preset-id="' + announcementId + '"][hidden] { display: block !important; }';
      css += '[data-section-type="announcement-bar"][data-preset-id="' + announcementId + '"] { display: block !important; }';
    }
    if (footerId) {
      css += '[data-section-type="footer"][data-preset-id="0"], [data-section-type="footer"][data-preset-id=""] { display: none !important; }';
      css += '[data-section-type="footer"][data-preset-id="' + footerId + '"][hidden] { display: block !important; }';
      css += '[data-section-type="footer"][data-preset-id="' + footerId + '"] { display: block !important; }';
    }
    if (sectionsId) {
      // Apply to preset-aware content sections.
      // Universal sections (empty preset_id) always show.
      // Default sections (preset_id 0) are hidden when a preset is active.
      // Sections with matching preset_id show.
      var sectionTypes = [
        'slideshow',
        'html',
        'liquid',
        'text-with-icons',
        '404-hero',
        'recently-viewed-products',
        'contact-section',
        'contact-activation-section',
        'work-cube-hero',
        'work-cube-intro',
        'work-cube-contact',
        'stand-out-event-system',
        'attract-engage-grow',
        'configure-ideal-setup',
        'testimonials-custom'
      ];
      sectionTypes.forEach(function(type) {
        // Hide default sections (preset_id 0) when preset is active
        css += '[data-section-type="' + type + '"][data-preset-id="0"] { display: none !important; }';
        // Show sections with matching preset_id
        css += '[data-section-type="' + type + '"][data-preset-id="' + sectionsId + '"][hidden] { display: block !important; }';
        css += '[data-section-type="' + type + '"][data-preset-id="' + sectionsId + '"] { display: block !important; }';
        // Universal sections (empty preset_id) stay visible - don't hide them
      });
    }

    if (css) {
      var style = document.createElement('style');
      style.id = 'preset-manager-flash-prevention';
      style.textContent = css;
      document.head.appendChild(style);
    }
  }

  /**
   * Apply custom font CSS variables
   */
  function applyCustomFonts(activePresets) {
    if (!activePresets) {
      return;
    }

    var headingFont = activePresets.custom_heading_font;
    var bodyFont = activePresets.custom_body_font;

    // Skip if no custom fonts
    if (!headingFont && !bodyFont) {
      return;
    }

    var css = ':root {';

    // Apply heading font
    if (headingFont && CUSTOM_FONTS[headingFont]) {
      var font = CUSTOM_FONTS[headingFont];
      css += '--heading-font-family: ' + font.family + ';';
      css += '--heading-font-weight: ' + 600 + ';';
      css += '--heading-font-style: ' + font.style + ';';
    }

    // Apply body/text font
    if (bodyFont && CUSTOM_FONTS[bodyFont]) {
      var font = CUSTOM_FONTS[bodyFont];
      css += '--text-font-family: ' + font.family + ';';
      css += '--text-font-weight: ' + font.weight + ';';
      css += '--text-font-style: ' + font.style + ';';
      css += '--text-font-bolder-weight: ' + font.bolderWeight + ';';
    }

    css += '}';

    var style = document.createElement('style');
    style.id = 'preset-manager-custom-fonts';
    style.textContent = css;
    document.head.appendChild(style);
  }

  /**
   * Load Typekit stylesheet for custom fonts if needed
   */
  function loadTypekitIfNeeded(activePresets) {
    if (!activePresets) {
      return;
    }

    var headingFont = activePresets.custom_heading_font;
    var bodyFont = activePresets.custom_body_font;

    // Check if any custom font requires Typekit
    var needsTypekit = (headingFont && CUSTOM_FONTS[headingFont]) ||
                       (bodyFont && CUSTOM_FONTS[bodyFont]);

    if (needsTypekit) {
      // Check if Typekit is already loaded
      var existingLink = document.querySelector('link[href*="use.typekit.net/dvn6imk"]');
      if (!existingLink) {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://use.typekit.net/dvn6imk.css';
        document.head.appendChild(link);
      }
    }
  }

  // Initialize and expose to window
  var activePresets = init();

  window.ezupPresetManager = {
    STORAGE_KEY: STORAGE_KEY,
    pagePresets: config.pagePresets,
    isHomepage: isHomepage,
    getStoredPresets: getStoredPresets,
    init: init,
    CUSTOM_FONTS: CUSTOM_FONTS
  };

  window.ezupActivePresets = activePresets;

  if (activePresets && activePresets.sections_id !== null && activePresets.sections_id !== undefined && activePresets.sections_id !== '') {
    document.documentElement.setAttribute('data-ezup-sections-preset', String(activePresets.sections_id));
  }

  // Apply flash prevention CSS
  injectFlashPreventionCSS(activePresets, config.pagePresets);

  // Load Typekit and apply custom fonts
  loadTypekitIfNeeded(activePresets);
  applyCustomFonts(activePresets);

  // Preset state is already persisted in sessionStorage. Do not append the
  // ezup_* presentation parameters to every internal link: doing so creates
  // crawlable URL duplicates for products, collections, PDFs, and articles.

})();
